import React, { useState, useCallback, useMemo, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, UserAddIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, PencilIcon, TrashIcon, LockClosedIcon } from '@heroicons/react/outline';
import TextField from './TextField';
import PhoneNumberField from './PhoneNumberField';
import RadioGroup from './RadioGroup';
import { apiGetGuardianNominationStatus } from '../utils/rmsApi';
import { PARTICIPANT_STATUS, PENDING_PARTICIPANT_STATUSES } from '../../constants/participantStatuses';

/**
 * NominationField
 * ---------------
 * Purpose:
 *   Renders a modal-based nomination flow for adding participants (e.g., Legal Guardian,
 *   additional responsible persons) to a referral. Supports both single and multiple
 *   nomination modes, providing a focused, clean UX for collecting detailed contact
 *   information with validation before committing.
 *
 * Schema structure expected:
 *   {
 *     "questionId": "guardian_nomination",
 *     "type": "nomination",
 *     "label": "...",
 *     "required": true,
 *     "meta": {
 *       "nominationType": "legal_guardian",
 *       "buttonLabel": "+ Add a parent / carer",
 *       "modalTitle": "Add Parent / Carer",
 *       "confirmButtonLabel": "Save",
 *       "successMessage": "An invitation will be sent to {email} when you complete this form.",
 *       "infoBanner": "Optional contextual info displayed above the title.",
 *       "instruction": "Detailed instruction displayed below title with required indicator.",
 *       "allowMultiple": false,      // Optional: if true, allows adding multiple nominations
 *       "maxItems": 6                // Optional: max number of nominations when allowMultiple is true
 *     },
 *     "children": [
 *       { "questionId": "guardian_first_name", "type": "text", ... },
 *       ...
 *     ]
 *   }
 *
 * Behaviour:
 *   - Displays an optional info banner (from meta.infoBanner) with contextual guidance
 *   - Initially shows a button to add a nominee
 *   - On click, opens a modal with all child fields
 *   - Validates email/phone confirmations match before allowing save
 *   - Single mode: stores flattened answers under parent questionId as an object
 *   - Multiple mode: stores array of nomination objects, displays as cards
 *   - Shows a success card with nominee details after saving
 *
 * Props:
 *   - question: The nomination question node from schema
 *   - value: Current stored value (object for single mode, array for multiple mode)
 *   - onChange: Callback to update the answer (receives nomination object/array)
 *   - schema: Full schema (for validator index if needed)
 *   - disabled: Whether the field is disabled
 *   - orgId: Organisation ID (for fetching nomination status)
 *   - patientRecordId: Patient record ID (for fetching nomination status)
 */
export default function NominationField({ question, value, onChange, schema, disabled = false, orgId, patientRecordId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  // Track which item index we're editing (-1 for new item in multiple mode)
  const [editingIndex, setEditingIndex] = useState(-1);
  
  // Nomination status state - determines if field is locked or shows warning
  // isLocked: true when guardian has accepted invite and linked account (cannot edit)
  // isPending: true when invite sent but not consumed (can edit, but show warning)
  const [nominationStatus, setNominationStatus] = useState({
    isLocked: false,
    isPending: false,
    status: null,
    loading: false,
    error: null
  });

  const meta = question?.meta || {};
  // Memoize children array to prevent dependency changes on every render
  // (used in multiple useCallback hooks below)
  const children = useMemo(() => question?.children || [], [question?.children]);
  const buttonLabel = meta.buttonLabel || '+ Add';
  const modalTitle = meta.modalTitle || 'Add Details';
  const confirmButtonLabel = meta.confirmButtonLabel || 'Save';
  const successMessage = meta.successMessage || 'Details saved successfully.';
  const infoBanner = meta.infoBanner || null;
  // Optional instruction text displayed below the title but above the add button
  const instruction = meta.instruction || null;
  // Multiple nominations support - defaults to false for backward compatibility
  const allowMultiple = meta.allowMultiple === true;
  const maxItems = meta.maxItems || 10;

  /**
   * Dynamically find field names for first name and email from children schema.
   * This allows the component to work with any field naming convention (e.g.,
   * guardian_first_name, lg_lr_first_name, etc.) instead of hardcoding.
   */
  const fieldNames = useMemo(() => {
    // Find fields by type and common naming patterns
    const firstNameField = children.find(c => 
      c.questionId.includes('first_name') || 
      (c.type === 'text' && c.label?.toLowerCase().includes('first'))
    );
    const lastNameField = children.find(c => 
      c.questionId.includes('last_name') || 
      (c.type === 'text' && c.label?.toLowerCase().includes('last'))
    );
    const emailField = children.find(c => c.type === 'email' && !c.questionId.includes('confirm'));
    const relationshipField = children.find(c => 
      c.questionId.includes('relationship') ||
      (c.label?.toLowerCase().includes('relationship'))
    );
    
    return {
      firstName: firstNameField?.questionId,
      lastName: lastNameField?.questionId,
      email: emailField?.questionId,
      relationship: relationshipField?.questionId
    };
  }, [children]);

  /**
   * Extract the email to check for status - memoized to prevent unnecessary re-fetches.
   * 
   * Performance optimization:
   * We only want to fetch nomination status when the EMAIL changes, not on every
   * value change (name, phone, relationship, etc.). By memoizing the email extraction,
   * the useEffect below will only trigger when the actual email value changes.
   */
  const emailToCheck = useMemo(() => {
    if (!allowMultiple && value && typeof value === 'object') {
      return fieldNames.email ? (value[fieldNames.email] || '').trim().toLowerCase() : null;
    } else if (allowMultiple && Array.isArray(value) && value.length > 0) {
      // For multiple mode, check the first nomination's email
      return fieldNames.email ? (value[0][fieldNames.email] || '').trim().toLowerCase() : null;
    }
    return null;
  }, [allowMultiple, value, fieldNames.email]);

  /**
   * Fetch nomination status when the saved nomination email changes.
   * 
   * Business logic:
   * - If the guardian has already accepted the invite (status === PARTICIPANT_STATUS.ACCOUNT_LINKED),
   *   the nomination is LOCKED and cannot be edited.
   * - If the invite is pending (PENDING_PARTICIPANT_STATUSES: 'nominated' or 'invite_sent'), the
   *   nomination can still be edited but we show a warning that an invite was already sent.
   * 
   * Performance optimizations:
   * 1. Only depends on emailToCheck (not entire value object) to minimize re-fetches
   * 2. Skips fetch if email hasn't changed or is empty
   * 3. Uses lastFetchedEmail ref to prevent duplicate fetches for same email
   */
  const lastFetchedEmailRef = React.useRef(null);
  
  useEffect(() => {
    let cancelled = false;
    
    const fetchStatus = async () => {
      // Skip if we don't have required data
      if (!orgId || !patientRecordId || !emailToCheck) {
        // Only reset if we had a previous email (avoid unnecessary state updates on initial render)
        if (lastFetchedEmailRef.current !== null) {
          setNominationStatus({
            isLocked: false,
            isPending: false,
            status: null,
            loading: false,
            error: null
          });
        }
        lastFetchedEmailRef.current = null;
        return;
      }
      
      // Skip if we already fetched for this exact email (cache hit)
      if (lastFetchedEmailRef.current === emailToCheck) {
        return;
      }
      
      setNominationStatus(prev => ({ ...prev, loading: true, error: null }));
      
      try {
        const result = await apiGetGuardianNominationStatus({
          orgId,
          patientRecordId,
          email: emailToCheck
        });
        
        if (cancelled) return;
        
        lastFetchedEmailRef.current = emailToCheck;
        setNominationStatus({
          isLocked: result.isLocked || false,
          isPending: result.isPending || false,
          status: result.status,
          loading: false,
          error: null
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[NominationField] Error fetching nomination status:', err);
        setNominationStatus({
          isLocked: false,
          isPending: false,
          status: null,
          loading: false,
          error: err.message || 'Failed to fetch status'
        });
      }
    };
    
    fetchStatus();
    
    return () => { cancelled = true; };
  }, [orgId, patientRecordId, emailToCheck]); // Only re-run when email changes, not entire value

  /**
   * For multiple mode, normalize value to array; for single mode, keep as object.
   * This handles backward compatibility and different data shapes.
   */
  const nominations = useMemo(() => {
    if (allowMultiple) {
      if (Array.isArray(value)) return value;
      if (value && typeof value === 'object' && Object.keys(value).length > 0) return [value];
      return [];
    }
    return value;
  }, [value, allowMultiple]);

  /**
   * Check if we have a saved nomination (single mode only).
   * For multiple mode, we check nominations.length instead.
   */
  const hasSavedNomination = useMemo(() => {
    if (allowMultiple) return false; // Multiple mode uses nominations array
    if (!value || typeof value !== 'object') return false;
    // Dynamically check for first name and email fields using detected field names
    const firstName = fieldNames.firstName ? value[fieldNames.firstName] : null;
    const email = fieldNames.email ? value[fieldNames.email] : null;
    return !!(firstName && email);
  }, [value, allowMultiple, fieldNames]);

  /**
   * Format success message with actual email from saved data.
   * Dynamically uses the detected email field name.
   */
  const formattedSuccessMessage = useMemo(() => {
    if (allowMultiple) return successMessage; // Multiple mode doesn't use this
    const email = fieldNames.email && value ? value[fieldNames.email] : null;
    if (!email) return successMessage;
    return successMessage.replace('{email}', email);
  }, [successMessage, value, allowMultiple, fieldNames]);

  /**
   * Open modal and initialize draft from current value or empty.
   * For multiple mode, can optionally pass an index to edit existing item.
   */
  const handleOpenModal = useCallback((indexToEdit = -1) => {
    if (disabled) return;
    
    // Determine which data to initialize from
    let sourceData = null;
    if (allowMultiple) {
      // Multiple mode: edit existing item or create new
      if (indexToEdit >= 0 && nominations[indexToEdit]) {
        sourceData = nominations[indexToEdit];
      }
      setEditingIndex(indexToEdit);
    } else {
      // Single mode: always edit the single value
      sourceData = value;
    }
    
    // Initialize draft from source data or empty object
    const initial = {};
    children.forEach(child => {
      initial[child.questionId] = sourceData?.[child.questionId] || '';
    });
    setDraft(initial);
    setErrors({});
    setTouched({});
    setIsOpen(true);
  }, [disabled, children, value, allowMultiple, nominations]);

  /**
   * Update a single field in the draft
   */
  const updateDraftField = useCallback((fieldId, fieldValue) => {
    setDraft(prev => ({ ...prev, [fieldId]: fieldValue }));
    setTouched(prev => ({ ...prev, [fieldId]: true }));
    // Clear error for this field when user types
    setErrors(prev => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  /**
   * Validate all fields before saving
   * Returns true if valid, false otherwise
   */
  const validateAll = useCallback(() => {
    const newErrors = {};

    children.forEach(child => {
      const val = draft[child.questionId];
      const strVal = String(val || '').trim();

      // Required check
      if (child.required && !strVal) {
        newErrors[child.questionId] = 'This field is required';
        return;
      }

      // Email pattern check
      if (child.type === 'email' && strVal) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(strVal)) {
          newErrors[child.questionId] = 'Please enter a valid email address';
          return;
        }
      }

      // Phone pattern check (basic)
      if (child.type === 'phone' && strVal) {
        const phonePattern = /^(?=(?:.*\d){10,})[0-9 +() -]{10,20}$/;
        if (!phonePattern.test(strVal)) {
          newErrors[child.questionId] = 'Please enter a valid phone number';
          return;
        }
      }

      // sameAs validator check (for confirmation fields)
      const sameAsValidator = (child.validators || []).find(v => v.type === 'sameAs');
      if (sameAsValidator && strVal) {
        const otherVal = String(draft[sameAsValidator.otherQuestionId] || '').trim();
        if (strVal !== otherVal) {
          newErrors[child.questionId] = 'Values do not match';
          return;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [children, draft]);

  /**
   * Handle save button click.
   * For single mode: saves draft directly.
   * For multiple mode: adds to array or updates existing item at editingIndex.
   */
  const handleSave = useCallback(() => {
    // Mark all fields as touched
    const allTouched = {};
    children.forEach(child => {
      allTouched[child.questionId] = true;
    });
    setTouched(allTouched);

    if (!validateAll()) {
      return;
    }

    if (allowMultiple) {
      // Multiple mode: add to array or update existing
      const currentArray = Array.isArray(nominations) ? [...nominations] : [];
      if (editingIndex >= 0) {
        // Update existing item
        currentArray[editingIndex] = draft;
      } else {
        // Add new item
        currentArray.push(draft);
      }
      onChange(currentArray);
    } else {
      // Single mode: save draft directly
      onChange(draft);
    }
    
    setIsOpen(false);
    setEditingIndex(-1);
  }, [children, validateAll, draft, onChange, allowMultiple, nominations, editingIndex]);

  /**
   * Handle remove/clear nomination.
   * For single mode: clears the value.
   * For multiple mode: removes item at specified index.
   */
  const handleRemove = useCallback((indexToRemove = -1) => {
    if (allowMultiple && indexToRemove >= 0) {
      // Multiple mode: remove specific item from array
      const currentArray = Array.isArray(nominations) ? [...nominations] : [];
      currentArray.splice(indexToRemove, 1);
      onChange(currentArray.length > 0 ? currentArray : []);
    } else {
      // Single mode: clear value
      onChange(null);
    }
  }, [onChange, allowMultiple, nominations]);

  /**
   * Render a field based on its type
   * 
   * Implementation notes:
   * - TextField passes the VALUE directly to onChange (not an event object)
   * - PhoneNumberField uses a different prop API (rawValue, onRawChange, onNormalizedChange)
   * - key prop must be passed directly to JSX, not spread via props object
   */
  const renderField = useCallback((child) => {
    const fieldId = child.questionId;
    const fieldValue = draft[fieldId] || '';
    const fieldError = touched[fieldId] ? errors[fieldId] : null;
    const isRequired = child.required;

    switch (child.type) {
      case 'text':
      case 'email':
        // TextField calls onChange with value directly (not event)
        return (
          <TextField
            key={fieldId}
            id={fieldId}
            label={child.label}
            required={isRequired}
            value={fieldValue}
            type={child.type === 'email' ? 'email' : 'text'}
            onChange={(val) => updateDraftField(fieldId, val)}
          />
        );

      case 'phone':
        // PhoneNumberField uses rawValue/onRawChange/onNormalizedChange API
        // We store both raw value (for display in field) and normalized value (with country code)
        // The normalized value is stored in a separate field for display in ReviewList
        return (
          <PhoneNumberField
            key={fieldId}
            label={child.label}
            required={isRequired}
            rawValue={fieldValue}
            onRawChange={(val) => updateDraftField(fieldId, val)}
            onNormalizedChange={(normalized) => {
              // Store the E.164 formatted number (with country code) in a separate field
              // This is used for display in ReviewList and for contacting the nominee
              updateDraftField(`${fieldId}_normalized`, normalized);
            }}
          />
        );

      case 'select':
        // Render as a native select dropdown
        return (
          <div key={fieldId} className="space-y-1">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700">
              {child.label}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={fieldId}
              value={fieldValue?.optionId || fieldValue || ''}
              onChange={(e) => {
                const selectedOption = (child.options || []).find(o => o.optionId === e.target.value);
                updateDraftField(fieldId, selectedOption ? { optionId: selectedOption.optionId, label: selectedOption.label } : '');
              }}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                fieldError ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={disabled}
            >
              <option value="">Select...</option>
              {(child.options || []).map(opt => (
                <option key={opt.optionId} value={opt.optionId}>
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Handle "Other" free text if selected */}
            {fieldValue?.optionId === 'other' && (child.options || []).find(o => o.optionId === 'other')?.allowsFreeText && (
              <input
                type="text"
                placeholder="Please specify..."
                value={fieldValue?.otherText || ''}
                onChange={(e) => updateDraftField(fieldId, { ...fieldValue, otherText: e.target.value })}
                className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={disabled}
              />
            )}
            {fieldError && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <ExclamationCircleIcon className="w-4 h-4" />
                {fieldError}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={fieldId} className="space-y-1">
            <RadioGroup
              id={fieldId}
              label={child.label}
              options={child.options || []}
              value={fieldValue}
              onChange={(val) => updateDraftField(fieldId, val)}
              required={isRequired}
              error={fieldError}
              disabled={disabled}
            />
          </div>
        );

      default:
        // Fallback to text field - TextField passes value directly to onChange
        return (
          <TextField
            key={fieldId}
            id={fieldId}
            label={child.label}
            required={isRequired}
            value={fieldValue}
            type="text"
            onChange={(val) => updateDraftField(fieldId, val)}
          />
        );
    }
  }, [draft, touched, errors, disabled, updateDraftField]);

  /**
   * Get display name for a nomination object.
   * Uses dynamically detected field names for flexibility.
   * Can be called with a specific nomination object for multiple mode.
   */
  const getDisplayName = useCallback((nominationData) => {
    if (!nominationData) return '';
    const first = fieldNames.firstName ? (nominationData[fieldNames.firstName] || '') : '';
    const last = fieldNames.lastName ? (nominationData[fieldNames.lastName] || '') : '';
    return `${first} ${last}`.trim() || 'Nominee';
  }, [fieldNames]);

  /**
   * Get relationship label for a nomination object.
   * Handles both string and object (from select field) values.
   */
  const getRelationship = useCallback((nominationData) => {
    if (!nominationData || !fieldNames.relationship) return '';
    const rel = nominationData[fieldNames.relationship];
    return typeof rel === 'object' ? rel.label : rel;
  }, [fieldNames]);

  /**
   * Get email for a nomination object.
   */
  const getEmail = useCallback((nominationData) => {
    if (!nominationData || !fieldNames.email) return '';
    return nominationData[fieldNames.email] || '';
  }, [fieldNames]);

  // For single mode backward compatibility
  const nomineeDisplayName = useMemo(() => getDisplayName(value), [getDisplayName, value]);
  const nomineeRelationship = useMemo(() => getRelationship(value), [getRelationship, value]);

  /**
   * Check if we can add more nominations (for multiple mode).
   * Returns false if maxItems limit is reached.
   */
  const canAddMore = useMemo(() => {
    if (!allowMultiple) return !hasSavedNomination;
    return nominations.length < maxItems;
  }, [allowMultiple, nominations, maxItems, hasSavedNomination]);

  return (
    <div className="space-y-3">
      {/* Info banner - displays contextual guidance from schema meta.infoBanner */}
      {/* Placed FIRST so users see context before the title and instruction */}
      {infoBanner && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 whitespace-pre-line">
              {infoBanner}
            </div>
          </div>
        </div>
      )}      

      {/* Instruction - detailed guidance with required indicator */}
      {instruction && (
        <p className="text-sm font-medium text-gray-800">
          {instruction}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </p>
      )}

      {/* Multiple mode: Show cards for each nomination + add button */}
      {allowMultiple ? (
        <div className="space-y-3">
          {/* Warning banner when invite is pending but not consumed (multiple mode) */}
          {nominationStatus.isPending && !nominationStatus.isLocked && nominations.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <ExclamationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  An invite has already been sent, but the recipient has not yet made their account.
                  You can still edit their details if needed.
                </p>
              </div>
            </div>
          )}
          
          {/* Locked banner when guardian has linked their account (multiple mode) */}
          {nominationStatus.isLocked && nominations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <LockClosedIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  This parent/carer has accepted the invitation and created their account. 
                  Their details can no longer be changed.
                </p>
              </div>
            </div>
          )}
          
          {/* Render card for each saved nomination */}
          {nominations.map((nomination, index) => (
            <div key={index} className={`rounded-lg p-4 ${
              nominationStatus.isLocked 
                ? 'bg-blue-50 border border-blue-200' 
                : 'bg-green-50 border border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                {nominationStatus.isLocked ? (
                  <LockClosedIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold ${nominationStatus.isLocked ? 'text-blue-800' : 'text-green-800'}`}>
                    {getDisplayName(nomination)}
                  </p>
                  {getRelationship(nomination) && (
                    <p className={`text-sm ${nominationStatus.isLocked ? 'text-blue-700' : 'text-green-700'}`}>
                      {getRelationship(nomination)}
                    </p>
                  )}
                  {getEmail(nomination) && (
                    <p className={`text-sm mt-1 ${nominationStatus.isLocked ? 'text-blue-600' : 'text-green-600'}`}>
                      {getEmail(nomination)}
                    </p>
                  )}
                </div>
                {/* Edit and Remove buttons - only shown when NOT locked */}
                {!nominationStatus.isLocked && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleOpenModal(index)}
                      disabled={disabled}
                      className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-md transition-colors disabled:opacity-50"
                      aria-label="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      aria-label="Remove"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Add button - shown if under maxItems limit and not locked */}
          {canAddMore && !nominationStatus.isLocked && (
            <button
              type="button"
              onClick={() => handleOpenModal(-1)}
              disabled={disabled}
              className="inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserAddIcon className="w-5 h-5" />
              <span className="font-medium">{buttonLabel}</span>
            </button>
          )}
          
          {/* Show limit reached message */}
          {!canAddMore && nominations.length >= maxItems && (
            <p className="text-sm text-gray-500 italic">
              Maximum of {maxItems} {maxItems === 1 ? 'person' : 'people'} reached.
            </p>
          )}
        </div>
      ) : (
        /* Single mode: Show saved nomination card OR add button */
        hasSavedNomination ? (
          <div className="space-y-3">
            {/* Warning banner when invite is pending but not consumed */}
            {nominationStatus.isPending && !nominationStatus.isLocked && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    An invite has already been sent to this person, but they have not yet made their account.
                    You can still edit their details if needed.
                  </p>
                </div>
              </div>
            )}
            
            {/* Locked banner when guardian has linked their account */}
            {nominationStatus.isLocked && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <LockClosedIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    This parent/carer has accepted the invitation and created their account. 
                    Their details can no longer be changed.
                  </p>
                </div>
              </div>
            )}
            
            {/* Nomination card - green when editable, blue when locked */}
            <div className={`rounded-lg p-4 ${
              nominationStatus.isLocked 
                ? 'bg-blue-50 border border-blue-200' 
                : 'bg-green-50 border border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                {nominationStatus.isLocked ? (
                  <LockClosedIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold ${nominationStatus.isLocked ? 'text-blue-800' : 'text-green-800'}`}>
                    {nomineeDisplayName}
                  </p>
                  {nomineeRelationship && (
                    <p className={`text-sm ${nominationStatus.isLocked ? 'text-blue-700' : 'text-green-700'}`}>
                      {nomineeRelationship}
                    </p>
                  )}
                  <p className={`text-sm mt-1 ${nominationStatus.isLocked ? 'text-blue-600' : 'text-green-600'}`}>
                    {getEmail(value)}
                  </p>
                  {!nominationStatus.isLocked && (
                    <p className="text-sm text-green-600 mt-2 italic">{formattedSuccessMessage}</p>
                  )}
                  {nominationStatus.isLocked && (
                    <p className="text-sm text-blue-600 mt-2 italic">Account created and linked to this referral.</p>
                  )}
                </div>
              </div>
              
              {/* Edit/Remove buttons - only shown when NOT locked */}
              {!nominationStatus.isLocked && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenModal()}
                    disabled={disabled}
                    className="text-sm text-primary hover:text-primary/80 hover:underline disabled:opacity-50"
                  >
                    Edit details
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => handleRemove()}
                    disabled={disabled}
                    className="text-sm text-red-600 hover:text-red-800 hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => handleOpenModal()}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserAddIcon className="w-5 h-5" />
            <span className="font-medium">{buttonLabel}</span>
          </button>
        )
      )}

      {/* Modal */}
      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="radix-dialog-content-centered-motion fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-[520px] max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
              <Dialog.Title className="text-lg md:text-xl font-bold text-gray-900">
                {modalTitle}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
              <Dialog.Description className="sr-only">
                Enter the details of the parent / carer to invite them to participate in the referral.
              </Dialog.Description>

              {children.map(renderField)}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 md:p-5 border-t border-gray-200 bg-gray-50">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                {confirmButtonLabel}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
